import { typographyTokens } from '../../../theme/typographyTokens';
import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import DepartmentIconRenderer, { DEPARTMENT_ICON_MAP } from '../../../assets/svg/departmentIconMap';
import UserIcon from '../../../assets/svg/UserIcon';
import { User } from '../../../../shared/types/User';
import { Department } from '../../../../shared/types/Department';

export interface ReviewerCardProps {
  reviewer?: User | null;
  department?: Department | null;
  isRequester?: boolean;
}

const ReviewerCard: React.FC<ReviewerCardProps> = ({ reviewer, department, isRequester }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const deptName = department?.name;
  const hasDeptIcon = !!deptName && !!DEPARTMENT_ICON_MAP[deptName];

  const displayName = reviewer?.givenName && reviewer?.surname
    ? `${reviewer.givenName} ${reviewer.surname}`
    : reviewer?.displayName || 'Unassigned';

  const iconColor = isDarkMode ? '#7EB6E8' : theme.palette.solidBlue.main;
  const badgeBg = isDarkMode ? 'rgba(255, 255, 255, 0.08)' : theme.palette.white.main;
  const badgeBorder = isDarkMode
    ? 'rgba(255, 255, 255, 0.15)'
    : theme.palette.gray.lightGray1;
  const nameColor = isDarkMode ? theme.palette.text.primary : theme.palette.content.default;
  const labelColor = iconColor;

  return (
    <Box
      sx={{
        display: 'flex',
        gap: '12px',
        alignItems: 'flex-start',
        minWidth: 0,
        width: '100%',
      }}
    >
      <Box
        sx={{
          width: '36px',
          height: '36px',
          borderRadius: '50%',
          backgroundColor: badgeBg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          boxShadow: isDarkMode ? 'none' : '0 2px 4px rgba(0,0,0,0.08)',
          border: `1px solid ${badgeBorder}`,
        }}
      >
        {isRequester ? (
          <UserIcon height={20} width={20} color={iconColor} />
        ) : hasDeptIcon ? (
          <DepartmentIconRenderer
            departmentName={deptName as string}
            height={20}
            width={20}
            color={iconColor}
          />
        ) : null}
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',
          minWidth: 0,
          flex: 1,
        }}
      >
        <Typography
          sx={{
            fontSize: typographyTokens.fontSizes.md,
            fontWeight: typographyTokens.fontWeights.semibold,
            color: nameColor,
            lineHeight: 1.3,
            overflowWrap: 'break-word',
            wordBreak: 'break-word',
          }}
        >
          {displayName}
        </Typography>
        <Typography
          sx={{
            fontSize: typographyTokens.fontSizes.sm,
            fontWeight: typographyTokens.fontWeights.medium,
            color: labelColor,
            textTransform: 'uppercase',
            lineHeight: 1.2,
            overflowWrap: 'break-word',
            wordBreak: 'break-word',
          }}
        >
          {isRequester ? 'Requester' : department?.name}
        </Typography>
      </Box>
    </Box>
  );
};

export default ReviewerCard;
